import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { useCancelTimeOffRequestDay } from '../../hooks/useCancelTimeOffRequestDay';

interface CancelDayActionProps {
  requestId: string;
  date: string;
  disabled?: boolean;
}

/** Requester only. A day that already started cannot be cancelled (`DayAlreadyStarted`, checked by the caller via `disabled`). */
export function CancelDayAction({ requestId, date, disabled }: CancelDayActionProps) {
  const { t } = useTranslation('timeOff');
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();
  const cancelDay = useCancelTimeOffRequestDay();

  async function handleClick() {
    const result = await confirm({
      title: t('requests.cancel.confirmDayTitle'),
      description: t('requests.cancel.confirmDayDescription'),
      variant: 'destructive',
    });
    if (!result.confirmed) return;
    cancelDay.mutate({ requestId, date });
  }

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleClick}
            disabled={disabled || cancelDay.isPending}
            aria-label={t('requests.cancel.confirmDayTitle')}
            data-testid={`time-off-request-cancel-day-btn-${date}`}
          >
            <X className="size-3.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('requests.cancel.confirmDayTitle')}</TooltipContent>
      </Tooltip>

      {isOpen && options && (
        <ConfirmDialog
          open={isOpen}
          onOpenChange={(open) => !open && handleCancel()}
          title={options.title}
          description={options.description}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
}
